import { Text } from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useState } from "react";
import DropdownTreeSelect from "react-dropdown-tree-select";
import "react-dropdown-tree-select/dist/styles.css";
import styled from "styled-components";
import { ReactComponent as ArrowDownIcon } from "../../../rawSVGs/ArrowDown.svg";
import { AcademicHierarchyService } from "../../../services/AcademicHierarchyService";
import { SubjectService } from "../../../services/SubjectService";
import { UserDetailContext } from "../../../utils/Context";

const StyledDropdownTreeSelect = styled(DropdownTreeSelect)`
  .dropdown-trigger {
    padding: 2px 4px !important;
    position: relative;
    display: block;
    box-sizing: border-box;
    border: 0.0625rem solid #c7cdd1 !important;
    border-radius: 0.25rem;
    background: #ffffff;
    color: #2d3b45 !important;
    cursor: pointer;
    min-width: 300px;
    height: 27px;
    overflow: hidden;
  }
  .dropdown-trigger.arrow.bottom:after {
    vertical-align: unset !important;
    content: "" !important;
    margin-right: 0 !important;
    width: 100%;
    height: 100%;
    display: inline-block;
    z-index: 1000;
    position: absolute;
    left: 0;
    top: 0;
    overflow: hidden;
  }
  .dropdown-trigger.arrow.top:after {
    vertical-align: unset !important;
    content: "" !important;
    margin-right: 0 !important;
    width: 100%;
    height: 100%;
    display: inline-block;
    z-index: 1000;
    position: absolute;
    left: 0;
    top: 0;
    overflow: hidden;
  }
  .tag-item {
    margin: 0 !important;
  }
  .tag-item.hide-me {
    display: none !important;
  }
  .search {
    border: none !important;
    font-size: 0.875rem !important;
    color: #2d3b45 !important;
    cursor: pointer;
  }
  .dropdown-content {
    border: 0.0625rem solid #c7cdd1 !important;
    border-radius: 0.25rem;
    border-width: 0.0625 rem;
    min-width: 290px;
    position: absolute;
    transform: translate(643px) translateY (-39px) translateZ(0);
    font-size: 0.875rem;
    max-height: 150px;
    overflow-y: scroll;
  }
  .dropdown {
    z-index: 1000;
  }
`;

const AcademicHierarchyMultiSelect = ({ selectHandler }) => {
  const userDetails = useContext(UserDetailContext);
  const [hierarchies, setHierarchies] = useState({});
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [countSelected, setCountSelected] = useState(0);
  const [originalHierarchy, setOriginalHierarchy] = useState({});
  const displayString = countSelected + " selected";
  const [flattenedTree, setFlattenedTree] = useState({});
  const [allSubjects, setAllSubjects] = useState([]);

  useEffect(() => {
    if (userDetails.role) {
      SubjectService.getSubjects().then((subs) => {
        setAllSubjects(subs);
      });
    }
  }, [userDetails]);

  useEffect(() => {
    if (allSubjects.length > 0) {
      AcademicHierarchyService.getLevelsFull([]).then((hierarchy) => {
        setOriginalHierarchy(hierarchy);
        let built_tree = buildTree(hierarchy);
        setHierarchies(built_tree);
        setFlattenedTree(flattenedHierarchies(built_tree));
      });
    }
  }, [allSubjects]);

  const getSubjects = (hierarchy_level) => {
    const subjects = [];
    if (hierarchy_level) {
      Object.keys(hierarchy_level).forEach((key) => {
        if (key !== "content") {
          if (hierarchy_level[key].content.label === "is academic subject") {
            const currentSubject = allSubjects.find(
              (sub) => sub.name === hierarchy_level[key].content.name
            );
            if (currentSubject) {
              subjects.push({ ...currentSubject, label: currentSubject.name });
            }
          } else if (hierarchy_level[key].content.label === "hierarchy level") {
            subjects.push(...getSubjects(hierarchy_level[key]));
          }
        }
      });
    }
    return subjects;
  };

  const buildTree = (hierarchy, selectionTracker = false) => {
    const tree = [];
    hierarchy.forEach((topLevel) => {
      const hierarchy_level = topLevel[Object.keys(topLevel)[0]];
      const subjects = getSubjects(hierarchy_level);
      if (subjects.length > 0) {
        const children = [];
        Object.keys(hierarchy_level).forEach((key) => {
          if (key === "content") {
            tree.push({
              label: hierarchy_level[key].name,
              value: hierarchy_level[key].id,
              expanded: true,
              tagClassName: "hide-me",
              checked:
                selectionTracker &&
                selectionTracker[hierarchy_level[key].id] &&
                selectionTracker[hierarchy_level[key].id].isChecked,
            });
          } else if (hierarchy_level[key].content.label === "hierarchy level") {
            const child = buildTree(
              [{ [key]: hierarchy_level[key] }],
              selectionTracker
            );
            if (child.length > 0) {
              children.push(child[0]);
            }
          }
        });
        if (userDetails.isAC()) {
          tree[tree.length - 1].children = children;
          tree[tree.length - 1].subjects = subjects;
        } else {
          let containsSubject = false;
          subjects.forEach((s) => {
            if (userDetails.ac_subjects.includes(s.title)) {
              containsSubject = true;
            }
          });
          if (containsSubject) {
            tree[tree.length - 1].children = children;
            tree[tree.length - 1].subjects = subjects;
          } else {
            tree.pop();
          }
        }
      }
    });
    return tree;
  };

  const getCheckedNodes = (flattened_hierarchy) => {
    let ar = Object.values(flattened_hierarchy).filter(
      (node) => node.isChecked
    );
    let num_checked = ar.length;
    return num_checked;
  };

  const logCheckedNodes = (selectedNodes) => {
    let selectionTracker = structuredClone(flattenedTree);
    selectedNodes.forEach((uppermostNode) => {
      selectionTracker[uppermostNode.value].isChecked = uppermostNode.checked;
      if (
        Object.keys(selectionTracker[uppermostNode.value].children).length === 0
      ) {
        let children = selectionTracker[uppermostNode.value].children;
        for (let child in children) {
          selectionTracker[child].isChecked = uppermostNode.checked;
        }
      }
    });
    return selectionTracker;
  };

  const onChange = (currentNode, selectedNodes) => {
    let selectionTracker = logCheckedNodes(selectedNodes);
    let num_checked = getCheckedNodes(selectionTracker);
    setCountSelected(num_checked);
    let new_tree = buildTree(originalHierarchy, selectionTracker);
    setHierarchies(new_tree);
    selectHandler(
      selectedNodes.map((node) => {
        return { id: node.value, name: node.label, subjects: node.subjects };
      })
    );
  };

  const getFlattenedChildren = (node, parent_path) => {
    let flattened_intermediate = {};
    if (node.children && node.children.length > 0) {
      let child_i = 0;
      node.children.forEach((child) => {
        let child_path = parent_path.concat([child_i]);
        flattened_intermediate[child.value] = {
          isChecked: false,
          label: child.label,
          children: getFlattenedChildren(child, child_path),
          path: child_path,
        };
        flattened_intermediate = {
          ...flattened_intermediate,
          ...getFlattenedChildren(child, child_path),
        };
        child_i += 1;
      });
    }
    return flattened_intermediate;
  };

  const flattenedHierarchies = (hierarchy) => {
    let flattened = {};
    let i = 0;
    hierarchy.forEach((topLevel) => {
      flattened[topLevel.value] = {
        isChecked: false,
        label: topLevel.label,
        children: getFlattenedChildren(topLevel, [i]),
        path: [i],
      };
      if (topLevel.children && topLevel.children.length > 0) {
        let i_sublevel = 0;
        topLevel.children.forEach((child) => {
          let child_path = flattened[topLevel.value].path.concat([i_sublevel]);
          let child_log = {
            [child.value]: {
              isChecked: false,
              label: child.label,
              children: getFlattenedChildren(child, child_path),
              path: child_path,
            },
          };
          flattened[child.value] = child_log[child.value];
          flattened = {
            ...flattened,
            ...getFlattenedChildren(child, child_log[child.value].path),
          };
          i_sublevel += 1;
        });
      }
      i += 1;
    });
    return flattened;
  };

  const DropDownTreeSelect = useMemo(() => {
    return (
      <StyledDropdownTreeSelect
        id="academic-hierarchy-select"
        texts={{ placeholder: displayString }}
        data={hierarchies}
        onChange={onChange}
        showPartiallySelected={true}
        onFocus={() => setTriggerOpen(true)}
        onBlur={() => setTriggerOpen(false)}
      />
    );
  }, [hierarchies]);

  return (
    <>
      <Text size="small" weight="bold">
        <label
          htmlFor="academic-hierarchy-select"
          style={{ display: "block", marginBottom: "0.8rem" }}
        >
          {t("academicHierarchy.headerTitle")}
        </label>
      </Text>
      <div style={{ position: "relative" }}>
        {DropDownTreeSelect}
        {triggerOpen ? (
          <ArrowDownIcon
            style={{
              position: "absolute",
              right: "0.8rem",
              top: "0.5rem",
              height: 12,
              width: 12,
              rotate: "180deg",
            }}
          />
        ) : (
          <ArrowDownIcon
            style={{
              position: "absolute",
              right: "0.8rem",
              top: "0.5rem",
              height: 12,
              width: 12,
            }}
          />
        )}
      </div>
    </>
  );
};

export default AcademicHierarchyMultiSelect;
